/**
 * @file BlogFeedCards.tsx
 * @description Вкладка «Лента RSS» панели «Блог» (phase19.16 Р1; SB39, SB75;
 * uiux-design-r1 §7): лента RSS (название, описание, адрес ленты со
 * «Скопировать») и подсказка «где фото автора». Поля страницы «Блог»
 * (шапка, поиск) — группами во вкладке «Лента» (`BlogFeedTab`, SB122).
 * Лимиты полей — как у сервера (контракт §13): название ленты до 120,
 * описание до 300.
 */

import { Copy } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { SectionCard } from "../../ui/SectionCard";
import { TextField } from "../TextField";
import { useCurrentContext } from "../../../auth/hooks";
import type { PartFieldsApi } from "../../../hooks/site-view/usePartFields";
import type { SiteBlogDraft } from "../../../lib/site-view/types";

interface Props {
  blog: PartFieldsApi<SiteBlogDraft>;
  siteName: string;
  siteUrl: string | null;
}

export function BlogRssCard({ blog, siteName, siteUrl }: Props) {
  const notify = useNotify();
  const { fields, set } = blog;
  const feedUrl = siteUrl ? `${siteUrl.replace(/\/+$/, "")}/blog/feed.xml` : null;
  return (
    <SectionCard title="Лента RSS">
      <TextField label="Название" value={fields.rssTitle} max={120} placeholder={`Блог ${siteName}`} hint={`Пусто — «Блог ${siteName}».`} onChange={(v) => set("rssTitle", v)} />
      <TextField label="Описание" value={fields.rssDescription} max={300} multiline onChange={(v) => set("rssDescription", v)} />
      {feedUrl ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-on-surface">Адрес ленты</span>
          <span className="type-body break-all font-mono text-on-surface">{feedUrl}</span>
          <Button
            variant="outline"
            className="self-start"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(feedUrl)
                .then(() => notify.success("Адрес ленты скопирован"))
                .catch(() => notify.error("Не удалось скопировать — выделите адрес вручную."))
            }
          >
            <Copy aria-hidden />
            Скопировать
          </Button>
          <p className="type-caption text-on-surface-variant">Для Дзена и программ чтения RSS.</p>
        </div>
      ) : (
        <p className="type-body text-on-surface-variant">У магазина пока нет своего адреса — лента появится после подключения домена.</p>
      )}
    </SectionCard>
  );
}

/** Подсказка «где фото автора». */
export function AuthorsHint() {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  return (
    <p className="type-caption text-on-surface-variant">
      Фото и соцсети автора — в{" "}
      <Link to={`/shop/${slug}/content/authors`} className="font-medium text-primary hover:underline">
        «Сайт → Авторы»
      </Link>
      : откройте автора.
    </p>
  );
}
